import { InfoCircleOutlined } from "@ant-design/icons";
import { notification } from "antd";
import cn from "classnames";
import Humanize from "humanize-plus";
// import { observer } from "mobx-react";
import moment from "moment";
import { useState, useMemo, useCallback, useRef } from "react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { VscGripper } from 'react-icons/vsc';

// import { FileUploadStatus } from "../../../enums";
// import { combineClasses, isEmpty } from "../../../utils";
import { useLoc } from "@app/shared/lib/hooks";
import { isEmpty } from "@app/shared/lib/is-empty";

import { DateTime } from "../../DateTime";
import { FileUploader, FileUploaderHandler } from "../../FileUploader";
import { FileActions } from "../components/FileActions/FileActions";
import { FileName } from "../components/FileName/FileName";
import { FilePreview } from "../components/FilePreview/FilePreview";
import { FileDescriptionPopover } from "../FileDescriptionPopover/FileDescriptionPopover";
import { FileUploadStatus } from "../FileListItem/FileListItem";
import { FileProps } from "../Files";

import styles from "./FileGridItem.module.scss";

export const FileGridItem: React.FC<FileProps> = ({ file, ...props }) => {
  const loc = useLoc();
  const uploaderHandler = useRef<FileUploaderHandler>(null);
  const [progress, setProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<FileUploadStatus>(
    file?.nativeFile != null
      ? FileUploadStatus.Uploading
      : FileUploadStatus.Uploaded,
  );

  const retryUpload = useCallback(() => {
    setProgress(0);
    setUploadStatus(FileUploadStatus.Uploading);
  }, []);

  const handleFileReadyToUpload = useCallback(async () => {
    const res = await props.beforeUpload?.(file);
    if (res !== undefined && res === false) return; // cancel uploading
    await uploaderHandler.current?.upload();
  }, []);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: file.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const fileContent = useMemo(
    () => (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(styles.fl_grid_item, "flex align-center p-5 relative")}
        data-size={props.size}
      >
        {/* Drag Handle - Top Center */}
        {props.draggable && (
          <div
            {...attributes}
            {...listeners}
            className={cn(styles.fl_drag_handle, 'z-10 cursor-grab')}
            style={{ cursor: 'grab' }}>
            <VscGripper />
          </div>
        )}

        {/* Background - Image or FileIcon */}
        <div className={styles.fl_background}>
          <FilePreview
            view="grid"
            file={file}
            fileProvider={props.fileProvider}
            size={props.size}
            progress={progress}
            uploadStatus={uploadStatus}
            onPreview={() => props.onPreview(file)}
            onRetryUpload={() => retryUpload()}
            fileIconColor={props.fileIconColor}
            className={styles.fl_background_preview}
          />
        </div>

        {/* Overlay with Actions - Only visible on hover */}
        <div className={styles.fl_overlay} onClick={() => props.onPreview(file)}>
          <div className={styles.fl_actions} onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}>
            <FileActions
              file={file}
              theme="light"
              size={props.size !== "xs" ? "middle" : "small"}
              deletable={props.deletable}
              downloadable={props.downloadable}
              replaceable={props.replaceable}
              uploadStatus={uploadStatus}
              onDownload={() => props.onDownload?.(file)}
              onPreview={() => props.onPreview(file)}
              onRemove={() => props.onRemove?.(file)}
              onReplace={() => props.onReplace?.(file)}
              onUpload={() => retryUpload()}
              onAbort={() => uploaderHandler.current?.abort()}
            />
          </div>
        </div>

        {/* Bottom Panel - File name and size */}
        <div className={styles.fl_bottom_panel}>
          <div className={styles.fl_file_name}>
            <FileName
              file={file}
              editable={props.editable}
              useExtension
              onRename={async (name) =>
                (await props.onChange?.(file, { name })) ?? true
              }
              className="text-center ellipsis"
            />
          </div>

          {file.size > 0 && (
            <div className={styles.fl_file_info}>
              {uploadStatus === FileUploadStatus.Error ? (
                <span className="fs-12 color-danger">
                  {loc.word("File.uploadFailedLabel", {
                    default: "Upload failed",
                  })}
                </span>
              ) : (
                <>
                  {(props.dateCreatedVisible === true ||
                    props.descriptionVisible === true) && (
                    <span className="flex-1 mr-5">
                      {props.dateCreatedVisible === true && (
                        <DateTime
                          date={file.createdDate}
                          format={
                            file.createdDate?.isSame(moment(), "day")
                              ? "LT"
                              : "L"
                          }
                          className="color-secondary"
                        />
                      )}

                      {props.descriptionVisible === true &&
                        uploadStatus === FileUploadStatus.Uploaded && (
                          <FileDescriptionPopover
                            file={file}
                            onSave={async (description) =>
                              (await props.onChange?.(file, { description })) ??
                              true
                            }
                          >
                            <InfoCircleOutlined
                              className={cn(
                                "ml-5",
                                !isEmpty(file.description)
                                  ? "color-success"
                                  : undefined,
                              )}
                            />
                          </FileDescriptionPopover>
                        )}
                    </span>
                  )}
                  <span className="color-secondary">
                    {Humanize.fileSize(file.size)}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    ),
    [file, loc, progress, props, retryUpload, uploadStatus],
  );

  return (
    <>
      {fileContent}

      {uploadStatus === FileUploadStatus.Uploading && (
        <FileUploader
          handler={(x) => {
            uploaderHandler.current = x;
          }}
          autostart={false}
          file={file}
          onReady={handleFileReadyToUpload}
          onProgress={(x) => setProgress(Math.min(x.percent, 90))}
          onComplete={async ({ file: completedFile }) => {
            completedFile.update(completedFile);
            setUploadStatus(FileUploadStatus.Uploaded);
            props.onUploaded?.(completedFile);
          }}
          uploadArgs={props.uploadArgs}
          onAbort={() => {
            if (file.nativeFile) {
              setUploadStatus(FileUploadStatus.Error);
              props.onAborted?.(file);
            }
          }}
          onError={() => {
            setUploadStatus(FileUploadStatus.Error);
            notification.error({
              message: loc.word("Error occured"),
              duration: 2,
            });
          }}
        />
      )}
    </>
  );
};
